import React from 'react';
import { inject, observer } from 'mobx-react';
import { feature } from 'topojson';
import * as d3 from 'd3';
import * as d3Geo from 'd3-geo';
import { Moment } from 'moment';
import * as _ from 'lodash';
import rootStore, { IMobxRootState } from '../../RootStore';
import GQMap from './GQMap';
import {
    IMapNode,
    IGetScoreDeltaQueryParams,
    VIEW,
    INote,
    ICircleData,
} from '../../interfaces';
import APIService, {
    IActiveGroup,
    IActiveCountry,
    IActiveDimension,
    E_RISK_TYPE
} from '../../services/APIService';
import EventService, { EventSubscription } from '../../services/EventsService';
import GQMapNoteAnchor, {
    IGQMapNoteAnchorData,
} from '../GQActivityPage/Insights/MapNoteAnchor';
import loading from '../../services/LoadingService';
import CustomWeightStore from '../../stores/CustomWeightStore';
import {
    first,
    flow,
    get,
    filter,
    includes,
    isNil,
} from 'lodash/fp';
import ChartToolbar from '../GQChart/ChartToolbar';
import { COMPOSITE_DATA_INTERVAL } from '../GQChart/ChartStore';
import { getRiskLongName } from '../../utils/riskUtils';
import { getMonthlyScoreDeltaDates } from '../../utils/scoreDeltaDates';
import { GQToastContainer } from 'components/GQToast/GQToastContainer';
import {
    C_NO_PERMISSIONS_RISK,
    C_SCORES_DATE_FORMAT,
} from '../../constants';
import { E_TOAST_TYPE } from 'components/GQToast/GQToast';

import wMap from '../../data/worldMap.json';
import { RouteComponentProps, withRouter } from '../withRouter';
import { IPreloadedMapApiData } from '../GQTemplateV2/exportInterfaces';
import { castComponentToMap } from '../../stores/SpecificActivityStore';

const projection = d3Geo
    .geoNaturalEarth1()
    .center([15, 25])
    .scale(260);
const features = feature(wMap, wMap.objects.land);
const geoPathFn = d3Geo.geoPath().projection(projection);

export interface ICustomMapComponentProps<T> {
    x: number;
    y: number;
    data?: T;
}

export interface ICustomMapComponent {
    countryID: number;
    Component: React.ComponentClass<ICustomMapComponentProps<any>>;
    data?: any;
}

interface IGQMapProviderState {
    nodes: IMapNode[];
    geoPath: string;
    showScores: boolean;
    showHalos: boolean;
}

export type IGQMapProviderProps = RouteComponentProps & {
    identifier?: E_RISK_TYPE;
    dateRangeStart?: Moment;
    dateRangeEnd?: Moment;
    isClientFacingIndicator?: boolean;
    riskId?: number;
    selectedCountries?: number[];
    virtualToday?: Moment;
    activeGroupId?: string;
    view: VIEW;
    annoMode?: boolean;
    annotations?: INote[];
    insightMapAnnotations?: INote[];
    willMount?: () => void;
    overrideCountriesList?: Set<number>;
    dimensions?: { [dimensionId: number]: IActiveDimension };
    clientWidth?: number;
    contentWidth?: number;
    isEventsFeedMinimized?: boolean;
    isRiskPickerMinimized?: boolean;
    updateChartDimensions?: () => void;
    hideToolbar?: boolean;
    preloadedData?: IPreloadedMapApiData;
    compositeDataInterval?: COMPOSITE_DATA_INTERVAL;
}

const CONTINGENT_SCORES_VIEWS = [
    VIEW.CONTINGENT,
    VIEW.SORT_BY_CONTINGENT_RISK,
    VIEW.SORT_BY_CONTINGENT_DELTA,
];

const RISK_SCORES_VIEWS = [
    VIEW.RISK_VIEW,
    VIEW.SORT_BY_RISK,
    VIEW.SORT_BY_DELTA,
];

const MONTHLY_TOGGLE_VIEWS = [
    VIEW.SORT_BY_RISK,
    VIEW.SORT_BY_DELTA,
    VIEW.SORT_BY_CONTINGENT_RISK,
    VIEW.SORT_BY_CONTINGENT_DELTA,
];

@inject(
    ({
        chartStore,
        risksStore,
        countryStore,
        settingsStore,
        insightsStore,
        eventsFeedStore,
    }: IMobxRootState, nextProps: IGQMapProviderProps): Partial<IGQMapProviderProps> => {
        // todo:eventually GQMapProvider should receive the countries list as a prop depending on each page's country list data structure
        // (eg - Contingent page will pass the first country + secondary countries list as a single array, while the map page will pass the selected country list array etc.)
        // GQMapProvider should not decide or know how to obtain its country list since this is a concern of each page in the app.
        // the current plan is to:
        // 1. make the GQMapProvider decide which country store to use
        // 2. convert HomeController mega-component into multiple components - each component per app page (eg: ContingentPage, WorldGrpahPage, CountryPage etc.)
        // 3. Each page will have (or not have) its own map component which will receive the countries list as a prop from the page component
        const displayedMapComponent = castComponentToMap(
            insightsStore.displayedComponent
        );

        const isSingleDateRankView =
            nextProps.view === VIEW.SORT_BY_RISK ||
            nextProps.view === VIEW.SORT_BY_CONTINGENT_RISK;

        return {
            identifier: nextProps.view === VIEW.RISK_VIEW ? E_RISK_TYPE.DIMENSIONS : risksStore.currentRisksType,
            riskId: nextProps.view === VIEW.RISK_VIEW ? risksStore.homepageRiskId : _.first(risksStore.currentList),
            isClientFacingIndicator: risksStore.isClientFacingIndicators,
            dateRangeStart: isSingleDateRankView
                ? chartStore.virtualTodayRangeStart
                : chartStore.rangeLeftDate.clone(),
            dateRangeEnd: isSingleDateRankView
                ? chartStore.virtualTodayRangeEnd
                : chartStore.rangeRightDate.clone(),
            selectedCountries: countryStore.currentCountryList,
            virtualToday: chartStore.virtualToday.clone(),
            activeGroupId: settingsStore.activeGroupId,
            annoMode: insightsStore.annotationMode,
            insightMapAnnotations: displayedMapComponent
                ? displayedMapComponent.annotations
                : undefined,
            clientWidth: chartStore.clientWidth,
            contentWidth: chartStore.width,
            isEventsFeedMinimized: eventsFeedStore.isMinimized,
            isRiskPickerMinimized: risksStore.isMinimized,
            updateChartDimensions: chartStore.updateDimensions,
            compositeDataInterval: chartStore.compositeDataInterval,
        };
    }
)
@observer
class GQMapProvider extends React.Component<
    IGQMapProviderProps,
    IGQMapProviderState
> {
    private mounted = false;
    private force: d3.Simulation<IMapNode, undefined> = null;
    private restartCollide: boolean = false;
    private onReloadCountryNotification: EventSubscription = null;
    private onStopForceSub: EventSubscription = null;
    private fetchScoresDebounced: () => Promise<void>;
    private toaster: GQToastContainer;

    constructor(props: IGQMapProviderProps) {
        super(props);
        this.state = {
            nodes: [],
            geoPath: geoPathFn(features as any),
            showScores: false,
            showHalos: false,
        };
        this.fetchScoresDebounced = _.debounce(this.fetchScores, 500);
    }

    public componentWillUnmount() {
        this.mounted = false;
        window.removeEventListener('resize', this.fetchScoresDebounced);
        if (this.onReloadCountryNotification) {
            this.onReloadCountryNotification.remove();
        }
        this.onStopForceSub.remove();
    }
    public componentDidMount() {
        if (this.props.willMount) {
            this.props.willMount();
        }
        this.onStopForceSub = EventService.addListener('MAP_STOP_FORCE', () => {
            if (this.force) {
                this.force.stop();
            }
        });

        this.mounted = true;
        this.fetchScores();
        this.onReloadCountryNotification = EventService.registerOnReloadCountry(
            () => this.fetchScores()
        );
        window.addEventListener('resize', this.fetchScoresDebounced);
    }

    public componentDidUpdate(
        prevProps: IGQMapProviderProps,
        prevState: IGQMapProviderState
    ) {
        if (
            this.shouldFetchRiskScores(prevProps, prevState) ||
            this.shouldFetchContingent(prevProps, prevState)
        ) {
            this.fetchScoresDebounced();
        }

        this.props.updateChartDimensions();
    }

    private shouldFetchContingent(
        prevProps: IGQMapProviderProps,
        prevState: IGQMapProviderState
    ) {
        if (!includes(this.props.view, CONTINGENT_SCORES_VIEWS)) {
            return false;
        }

        return (
            !includes(prevProps.view, CONTINGENT_SCORES_VIEWS)  ||
            this.props.dateRangeStart.format(C_SCORES_DATE_FORMAT) !==
                prevProps.dateRangeStart.format(C_SCORES_DATE_FORMAT) ||
            this.props.dateRangeEnd.format(C_SCORES_DATE_FORMAT) !==
                prevProps.dateRangeEnd.format(C_SCORES_DATE_FORMAT) ||
            this.props.virtualToday.format(C_SCORES_DATE_FORMAT) !==
                prevProps.virtualToday.format(C_SCORES_DATE_FORMAT) ||
            this.props.selectedCountries[0] !==
                prevProps.selectedCountries[0] ||
            this.props.riskId !== prevProps.riskId
        );
    }


    private getMapTitle = () => {
        return getRiskLongName(
            this.props.identifier,
            this.props.riskId
        ) || '';
    };

    public render() {
        return (
            <>
                <GQToastContainer ref={el => (this.toaster = el)} />
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        width: '100%',
                    }}>
                    {!this.props.hideToolbar && (
                        <ChartToolbar
                            title={this.getMapTitle()}
                            hideMonthlyToggle={
                                !includes(
                                    this.props.view,
                                    MONTHLY_TOGGLE_VIEWS
                                )
                            }
                            hideActivePreset
                            hideCompareButton
                        />
                    )}
                    <GQMap
                        geoPath={this.state.geoPath}
                        nodes={this.state.nodes}
                        showScores={this.state.showScores}
                        showHalos={this.state.showHalos}
                        view={this.props.view}
                        selectedCountries={this.props.selectedCountries}
                        onCircleClick={this.handleCircleClick}
                        onCircleHover={this.toggleHover}
                        colorToSelectedIdentifier={this.getColorForCountry}
                        className={this.props.annoMode ? 'anno-mode' : ''}
                        customComponents={this.resolveCustomComponents()}
                        isSmallScreen={this.isSmallScreen()}
                        contentWidth={this.props.contentWidth}
                    />
                </div>
            </>
        );
    }

    private resolveCustomComponents(): ICustomMapComponent[] {
        const annotations =
            this.props.annotations || this.props.insightMapAnnotations;
        if (annotations) {
            const notes = _.map(annotations, (note, i) => {
                const data: IGQMapNoteAnchorData = {
                    index: i + 1,
                };
                const circle: ICustomMapComponent = {
                    Component: GQMapNoteAnchor,
                    countryID: note.countryID,
                    data,
                };
                return circle;
            });
            return notes;
        }
    }

    private getColorForCountry = (id: number) => {
        const color = rootStore.countryStore.colorStack.color(id);
        return color;
    };

    private handleCircleClick = (
        id: number,
        e: React.MouseEvent<SVGElement>
    ) => {
        const { annoMode, view } = this.props;
        if (annoMode) {
            rootStore.annoStore.selectedMapCountry = id;
        } else if (view === VIEW.RISK_VIEW) {
            this.navigateToCountryGraph(id);
        } else if (view === VIEW.CONTINGENT) {
            rootStore.countryStore.toggleSecondaryCountry(id);
        } else {
            this.toggleCountry(id);
        }
    };
    private toggleCountry = (id: number) => {
        rootStore.countryStore.toggleCountry(id);
    };

    private toggleHover = async (
        id: number,
        e: React.MouseEvent<SVGGElement>
    ) => {};

    private fetchScores = async () => {
        loading.start();
        const isContingent = includes(this.props.view, CONTINGENT_SCORES_VIEWS);
        const isGQ = isContingent ? true : !CustomWeightStore.hasActiveCustomWeight;
        const riskType = this.props.identifier; // Fundamental, Focus or Contingent;
        const riskId = this.props.riskId;
        const countryId = first(this.props.selectedCountries || []);
        const { virtualToday, dateStart, dateEnd } = this.getEffectiveDates();
        const params: IGetScoreDeltaQueryParams = {
            riskType,
            riskId,
            countryId,
            virtualToday,
            dateStart,
            dateEnd,
        };

        const groups = this.props.preloadedData ? this.props.preloadedData.groups : await APIService.getActiveGroups();
        
        let scoreDeltas: ICircleData[];
        if(this.props.preloadedData){
            scoreDeltas = this.props.preloadedData.scoreDeltas
        }
        else {
            try {
                scoreDeltas = await APIService.getScoreDelta(params, isGQ);
            } catch (e) {
                loading.stop();
                if (403 === get(['response', 'status'], e)){
                    this.toaster.createToast(C_NO_PERMISSIONS_RISK, {
                        type: E_TOAST_TYPE.ERROR,
                    });
                }
                return;
            }
        }

        const activeGroup = flow(
            get('groups'),
            filter(
                (group: IActiveGroup) =>
                    (isNil(group.id) && isNil(this.props.activeGroupId)) ||
                    group.id === this.props.activeGroupId
            ),
            first
        )(groups);

        if (this.props.overrideCountriesList) {
            scoreDeltas = scoreDeltas.filter(c =>
                this.props.overrideCountriesList.has(c.geo.id)
            );
        }
        if (this.mounted) {
            const nodes = this.convertNodes(scoreDeltas, activeGroup);
            const geoData = this.props.preloadedData ? this.props.preloadedData.geoData : await APIService.getActiveCountries();
            const focal: IActiveCountry =
                geoData.countries[first(this.props.selectedCountries)];
            if (this.props.view === VIEW.CONTINGENT) {
                nodes.push({
                    riskValue: 1,
                    radius: 19,
                    posX: projection
                        ? +projection([focal.longitude, focal.latitude])[0]
                        : focal.longitude,
                    posY: projection
                        ? +projection([focal.longitude, focal.latitude])[1]
                        : focal.latitude,
                    x: projection
                        ? +projection([focal.longitude, focal.latitude])[0]
                        : focal.longitude,
                    y: projection
                        ? +projection([focal.longitude, focal.latitude])[1]
                        : focal.latitude,
                    name: focal.name,
                    original_name: focal.name,
                    id: focal.id,
                    abbreviation: focal.abbreviation,
                    abbreviation_short: focal.abbreviation_short,
                });
            }
            this.applyForce(nodes);
        }
        loading.stop();
    };

    private convertNodes(
        scoreDeltas: ICircleData[],
        activeGroup: IActiveGroup
    ) {
        const nodes: IMapNode[] = [];

        for (const d of scoreDeltas) {
            const activeGroupCountriesHasDelta = flow(
                get('countries'),
                includes(d.geo.id)
            )(activeGroup);
            if (!activeGroup || activeGroupCountriesHasDelta) {
                nodes.push({
                    delta: d.delta,
                    delta_type: d.delta > 0 ? 'up' : (d.delta < 0 ? "down" : "same"),
                    class:
                        this.props.view === VIEW.RISK_VIEW
                            ? 'hide'
                            : d.delta > 0
                            ? 'negative-circle'
                            : d.delta < 0
                            ? 'positive-circle'
                            : 'hide',
                    id: d.geo.id,
                    name: d.geo.name,
                    opacity: d.delta_distance,
                    radius: this.calcRadius(d.score_size),  // Size of circle in CR map & sort by rank views.
                    deltaRadius: Math.max(d.delta_distance, 0.2),  // Size of circle in sort by delta view.
                    posX: projection
                        ? +projection([d.geo.longitude, d.geo.latitude])[0]
                        : d.geo.longitude,
                    posY: projection
                        ? +projection([d.geo.longitude, d.geo.latitude])[1]
                        : d.geo.latitude,
                    score: d.score_today,
                    abbreviation_short: d.geo.abbreviation_short,
                    abbreviation: d.geo.abbreviation,
                    x: projection
                        ? +projection([d.geo.longitude, d.geo.latitude])[0]
                        : d.geo.longitude,
                    y: projection
                        ? +projection([d.geo.longitude, d.geo.latitude])[1]
                        : d.geo.latitude,
                });

                nodes[nodes.length - 1].name =
                    nodes[nodes.length - 1].name.length * 3 >
                    nodes[nodes.length - 1].radius - 5
                        ? d.geo.abbreviation_short
                        : nodes[nodes.length - 1].name;
                if (nodes[nodes.length - 1].name === 'GB') {
                    nodes[nodes.length - 1].name = 'UK';
                }
            }
        }
        switch (this.props.view) {
            case VIEW.SORT_BY_RISK:
            case VIEW.SORT_BY_CONTINGENT_RISK:
                const linearNodes = _.sortBy(nodes, d => -d.score);
                const maxRadi = _.maxBy(linearNodes, d => d.radius);
                let xBuffer = 5;
                let yBuffer = 0;
                for (const node of linearNodes) {
                    if (node.radius * 2 + xBuffer > 1000) {
                        yBuffer += maxRadi.radius * 2;
                        xBuffer = 5 + (maxRadi.radius - node.radius);
                    }
                    node.posX = xBuffer + node.radius;
                    node.posY = yBuffer + 50;
                    xBuffer += (node.radius + 2) * 2;
                }
                return linearNodes;
            case VIEW.SORT_BY_DELTA:
            case VIEW.SORT_BY_CONTINGENT_DELTA:
                const width = 65;
                const height = 90;
                const negativeNodes = _.sortBy(
                    _.filter(nodes, node => node.class === 'negative-circle'),
                    node => -node.delta
                );
                const positiveNodes = _.sortBy(
                    _.filter(nodes, node => node.class === 'positive-circle'),
                    node => -node.delta
                );
                const deltaNodes = [...negativeNodes, ...positiveNodes];
                const paddingRight = 16;
                let deltaX = 0;
                let deltaY = 5;
                for (const node of deltaNodes) {
                    if (
                        deltaX + width >
                        this.props.contentWidth - paddingRight
                    ) {
                        deltaY += height;
                        deltaX = 0;
                    }
                    node.posX = deltaX;
                    node.posY = deltaY;
                    deltaX += width;
                }
                return deltaNodes;
        }
        return nodes;
    }

    private isSmallScreen() {
        return this.props.clientWidth <= 1500;
    }
    private calcRadius(initialRadius: number): number {
        switch (this.props.view) {
            case VIEW.CONTINGENT: {                
                return 12.5 + 12.5 * initialRadius
            }
            case VIEW.RISK_VIEW: {
                return this.isSmallScreen() ? 22 : 18;
            }
            case VIEW.SORT_BY_RISK:
            case VIEW.SORT_BY_CONTINGENT_RISK: {
                const multiplier = 20;
                return initialRadius * multiplier + 19;
            }
            default: {
                const multiplier = 35;
                return initialRadius * multiplier + 19;
            }
        }
    }

    private applyForce(nodes: IMapNode[]) {
        const forceParams = {
            collide: this.isGeo ? (this.restartCollide ? 0 : 0.5) : 0,
            XY: this.isGeo ? 0.1 : 0.15,
            alpha: this.restartCollide ? 0.5 : 1,
            alphaMin: 0.3,
        };
        if (this.force) {
            _.forEach(nodes, item => {
                const forceNode = _.first(
                    _.filter(
                        this.force.nodes(),
                        forceItem => forceItem.name === item.name
                    )
                );
                if (forceNode && forceNode.x && forceNode.y) {
                    delete item.x;
                    delete item.y;
                    Object.assign(item, {
                        x: forceNode.x,
                        y: forceNode.y,
                    });
                }
            });
            this.force.nodes(nodes);
            this.force.alpha(forceParams.alpha);
        } else {
            const width = this.isDelta ? this.props.contentWidth : 960;
            const height = 600;
            this.force = d3.forceSimulation(nodes);
            this.force
                .alphaMin(forceParams.alphaMin)
                .on('tick', () => {
                    if (this.isGeo) {
                        _.forEach(this.force.nodes(), node => {
                            node.x = Math.max(
                                node.radius,
                                Math.min(width - node.radius, node.x)
                            );
                            node.y = Math.max(
                                node.radius,
                                Math.min(height - node.radius, node.y)
                            );
                        });
                    }
                    if (
                        this.mounted &&
                        this.props.view !== VIEW.EXPORT_COMPONENT
                    ) {
                        this.setState({
                            showScores: this.isDelta,
                            showHalos: false,
                            nodes: this.force.nodes().slice(),
                        });
                    }
                })
                .on('end', () => {
                    if (this.restartCollide) {
                        this.restartCollide = false;
                        this.force.alpha(1);
                        this.force.alphaMin(0.2);
                        this.force.force(
                            'collide',
                            d3
                                .forceCollide()
                                //@ts-ignore
                                .radius(this.collisionRadiFn)
                                .strength(0.2)
                                .iterations(6)
                        );
                        this.force.restart();
                        return;
                    }
                    if (this.mounted) {
                        this.setState(
                            {
                                showScores: true,
                                showHalos: true,
                                nodes: this.force.nodes().slice(),
                            },
                        );
                    }
                });
        }
        this.force
            .force(
                'x',
                d3.forceX((d: IMapNode) => d.posX).strength(forceParams.XY)
            )
            .force(
                'y',
                d3.forceY((d: IMapNode) => d.posY).strength(forceParams.XY)
            )
            .force(
                'collide',
                d3
                    .forceCollide()
                    //@ts-ignore
                    .radius(this.collisionRadiFn)
                    .strength(forceParams.collide)
                    .iterations(6)
            );
        this.force.restart();
    }

    private collisionRadiFn = (d: IMapNode) => {
        const alpha = this.force.alpha();
        const alphaMin = this.force.alphaMin();

        // workaround, since initially the opacity parameter was required to prevent nodes from colliding
        const opacity = this.props.view === VIEW.CONTINGENT ? 0.5 : d.opacity;
        const scoreCollision = Math.max(opacity * 10, 2);

        if (alpha > alphaMin) {
            const newRadi = Math.floor(
                d.radius * (this.force.alphaMin() / this.force.alpha())
            );
            return (Math.max(d.radius, newRadi) + scoreCollision) * 1.1;
        }

        return (d.radius + scoreCollision) * 1.1;
    };

    private shouldFetchRiskScores(
        prevProps: IGQMapProviderProps,
        prevState: IGQMapProviderState
    ) {
        if (!includes(this.props.view, RISK_SCORES_VIEWS)) {
            return false;
        }
        if (
            this.props.compositeDataInterval !== prevProps.compositeDataInterval
        ) {
            return true;
        }
        if (this.props.identifier !== prevProps.identifier) {
            return true;
        }
        if (
            this.props.isClientFacingIndicator !==
            prevProps.isClientFacingIndicator
        ) {
            return true;
        }
        if (
            this.props.virtualToday.format(C_SCORES_DATE_FORMAT) !==
            prevProps.virtualToday.format(C_SCORES_DATE_FORMAT)
        ) {
            return true;
        }
        if (
            this.props.dateRangeStart.format(C_SCORES_DATE_FORMAT) !==
                prevProps.dateRangeStart.format(C_SCORES_DATE_FORMAT) ||
            this.props.dateRangeEnd.format(C_SCORES_DATE_FORMAT) !==
                prevProps.dateRangeEnd.format(C_SCORES_DATE_FORMAT)
        ) {
            return true;
        }
        if (this.props.view !== prevProps.view) {
            this.restartCollide = this.props.view === VIEW.RISK_VIEW;
            return true;
        }
        if (this.props.riskId !== prevProps.riskId) {
            return true;
        }
        if (this.props.activeGroupId !== prevProps.activeGroupId) {
            return true;
        }
        if (
            this.props.overrideCountriesList !== prevProps.overrideCountriesList
        ) {
            return true;
        }
        if (
            this.props.isEventsFeedMinimized !==
                prevProps.isEventsFeedMinimized ||
            this.props.isRiskPickerMinimized !== prevProps.isRiskPickerMinimized
        ) {
            return true;
        }

        return false;
    }

    private get isGeo() {
        return (
            this.props.view === VIEW.RISK_VIEW ||
            this.props.view === VIEW.CONTINGENT
        );
    }

    private get isDelta() {
        return (
            this.props.view === VIEW.SORT_BY_DELTA ||
            this.props.view === VIEW.SORT_BY_CONTINGENT_DELTA
        );
    }

    private get isMonthly() {
        return (
            this.props.compositeDataInterval ===
                COMPOSITE_DATA_INTERVAL.MONTHLY &&
            includes(this.props.view, MONTHLY_TOGGLE_VIEWS)
        );
    }

    private getEffectiveDates() {
        const virtualToday = this.props.virtualToday;
        const dateStart = this.props.dateRangeStart;
        const dateEnd = this.props.dateRangeEnd;
        if (!this.isMonthly) {
            return { virtualToday, dateStart, dateEnd };
        }
        return getMonthlyScoreDeltaDates({ virtualToday, dateStart, dateEnd });
    }

    private navigateToCountryGraph(countryId: number) {
        rootStore.countryStore.changeFirstCountry(countryId);
        this.props.navigate(`/${VIEW.COUNTRY_GRAPH}`)
    }
}

export default withRouter(GQMapProvider)
